// REVOPS-1 (2026-06-04) — Operations → Invoice Prep (Admin / Accounting).
// Prep queue (submitted packages) → prepare invoice (allocation modes, admin fee
// = configurable % of service, System Settings) → invoice lifecycle.
// Gated manage_invoicing; TPA-walled server-side.
import { useState, useEffect, useCallback } from "react";
import { apiFetch, fmt, can } from "../api";
import { Spinner } from "./ui";  // UI-SWEEP-ACCOUNTING
import { downloadReimbExport } from "./Reimbursements";
import { useNavigation } from "./nav/NavigationContext";
import { StatusPill } from "./ui";
import EntityDocuments from "./EntityDocuments";  // DC-4b: invoice support docs
import { EmailSentConfirmation } from "./ComposeEmailModal";  // post-send confirmation (2026-07-05)

// GLOBAL-NAV-2: opening an invoice is a first-class navigation (breadcrumb +
// history + recents/pins), not internal list state.
const openInvoiceLoc = (id) => ({ page: "invoice", entityType: "invoice", entityId: id, params: { invId: id } });

const ALLOC = { single: "Single / merged invoice", slip_split: "Split across this matter's slip",
  follow_ia_slip: "Follow related IA claim's slip (TPA)", manual: "Manual allocation" };
const INV_STATUS_COLOR = { Draft: ["#f1f5f9", "#475569"], Prepared: ["#e0e7ff", "#3730a3"], Issued: ["#ddd6fe", "#5b21b6"],
  Sent: ["#dbeafe", "#1e40af"], "Partially Paid": ["#fef3c7", "#92400e"], Paid: ["#dcfce7", "#166534"],
  Disputed: ["#fed7aa", "#9a3412"], "Written Off": ["#e2e8f0", "#475569"], Void: ["#fee2e2", "#991b1b"] };

function Pill({ s }) { return <StatusPill status={s} title={s} />; }
function M({ v, b }) { return <span style={{ fontFamily: "JetBrains Mono,monospace", fontWeight: b ? 700 : 400 }}>{fmt.money(v)}</span>; }

// ── Sticky + sortable table headers (2026-06-05) — shared across the three invoice tabs.
// useSort holds {key,dir}; apply() returns a sorted copy (numeric-aware, nulls last, ISO
// dates sort chronologically as strings). SortTh renders a clickable, sticky <th> with an
// active-direction caret. Wrap the <table> in <div style={stickyWrap}> so the header sticks
// within its own scroll area (not under the app nav).
function useSort(initialKey, initialDir = "asc") {
  const [sort, setSort] = useState({ key: initialKey, dir: initialDir });
  const toggle = (key) => setSort(s => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const apply = (rows) => {
    if (!sort.key) return rows;
    const d = sort.dir === "asc" ? 1 : -1;
    return [...(rows || [])].sort((a, b) => {
      let x = a[sort.key], y = b[sort.key];
      if (x == null || x === "") return 1;
      if (y == null || y === "") return -1;
      const nx = Number(x), ny = Number(y);
      if (!isNaN(nx) && !isNaN(ny)) return (nx - ny) * d;
      return String(x).localeCompare(String(y)) * d;
    });
  };
  return { sort, toggle, apply };
}
const stickyWrap = { maxHeight: "62vh", overflow: "auto" };
function SortTh({ label, k, sort, toggle, align }) {
  const active = sort.key === k;
  const style = {
    padding: "8px 10px", textAlign: align || "left", fontSize: 11, color: active ? "var(--navy)" : "var(--text3)",
    textTransform: "uppercase", cursor: k ? "pointer" : "default", userSelect: "none", whiteSpace: "nowrap",
    position: "sticky", top: 0, zIndex: 1, background: "#f1f5f9", borderBottom: "1px solid var(--border)",
  };
  return <th style={style} onClick={() => k && toggle(k)}>{label}{active ? (sort.dir === "asc" ? " ▲" : " ▼") : (k ? " ↕" : "")}</th>;
}

export function InvoicePrepWorkspace({ onNavigateToClaim }) {
  const [view, setView] = useState("queue");
  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {[["queue", "Prep Queue"], ["invoices", "Invoices"], ["ar", "AR / Aging"]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} style={{ padding: "5px 14px", fontSize: 13, fontWeight: 600, borderRadius: 16, cursor: "pointer",
            border: "1px solid " + (view === k ? "var(--navy)" : "var(--border)"), background: view === k ? "var(--navy)" : "transparent", color: view === k ? "#fff" : "var(--text2)" }}>{l}</button>
        ))}
      </div>
      {view === "queue" ? <PrepQueue onNavigateToClaim={onNavigateToClaim} />
        : view === "invoices" ? <InvoiceList onNavigateToClaim={onNavigateToClaim} />
        : <ARView onNavigateToClaim={onNavigateToClaim} />}
    </div>
  );
}

const AR_BUCKETS = ["Current", "1-30", "31-60", "61-90", "90+"];

function ARView({ onNavigateToClaim }) {
  const [data, setData] = useState({ rows: [], summary: { buckets: {} } });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const { navigate } = useNavigation();
  const load = useCallback(() => { setLoading(true); apiFetch("/reports/invoice-ar").then(d => { setData(d); setErr(null); }).catch(e => setErr(String(e.message || e))).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);
  const { sort, toggle, apply } = useSort("days_overdue", "desc");
  const td = { padding: "8px 10px", fontSize: 13 };
  const sum = data.summary || { buckets: {} };
  const ql = q.trim().toLowerCase();
  const arRows = (data.rows || []).filter(r => !ql || (r.invoice_number || "").toLowerCase().includes(ql) || (r.claim_number || "").toLowerCase().includes(ql));
  return (
    <div>
      {err && <div className="card" style={{ marginBottom: 12 }}><div className="card-body" style={{ color: "var(--red)" }}>{err}</div></div>}
      {/* Aging summary band */}
      <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 150px", background: "var(--navy)", color: "#fff", borderRadius: 8, padding: "12px 16px" }}>
          <div style={{ fontSize: 11, opacity: 0.8, textTransform: "uppercase" }}>Total Outstanding</div>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "JetBrains Mono,monospace" }}>{fmt.money(sum.total_outstanding || 0)}</div>
          <div style={{ fontSize: 11, opacity: 0.8 }}>{sum.count || 0} open invoice(s)</div>
        </div>
        {AR_BUCKETS.map(b => (
          <div key={b} style={{ flex: "1 1 110px", background: b === "90+" ? "#fef2f2" : "#fff", border: "1px solid " + (b === "90+" ? "#fecaca" : "var(--border)"), borderRadius: 8, padding: "12px 14px" }}>
            <div style={{ fontSize: 11, color: b === "90+" ? "#991b1b" : "var(--text3)", textTransform: "uppercase" }}>{b === "Current" ? "Current" : b + " days"}</div>
            <div style={{ fontSize: 17, fontWeight: 700, fontFamily: "JetBrains Mono,monospace", color: b === "90+" ? "#991b1b" : "var(--text)" }}>{fmt.money((sum.buckets || {})[b] || 0)}</div>
          </div>
        ))}
      </div>
      <div className="card">
        <div className="section-header"><span className="section-title">Open Receivables ({arRows.length})</span>
          <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search invoice # / claim #…" style={{ ...SELS, width: 220 }} />
            <span style={{ fontSize: 11, color: "var(--text3)" }}>Includes invoices billed in VCA</span></span></div>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? <Spinner label="Loading AR…" /> : arRows.length === 0 ?
            <div className="empty-state" style={{ padding: 24 }}>{q ? "No receivables match." : "No open receivables. 🎉"}</div> : (
            <div style={stickyWrap}><table style={{ width: "100%", fontSize: 13 }}>
              <thead><tr>
                <SortTh label="Invoice #" k="invoice_number" sort={sort} toggle={toggle} />
                <SortTh label="Claim" k="claim_number" sort={sort} toggle={toggle} />
                <SortTh label="Due" k="due_date" sort={sort} toggle={toggle} />
                <SortTh label="Days Past Due" k="days_overdue" sort={sort} toggle={toggle} align="right" />
                <SortTh label="Total" k="invoice_total" sort={sort} toggle={toggle} align="right" />
                <SortTh label="Paid" k="paid" sort={sort} toggle={toggle} align="right" />
                <SortTh label="Outstanding" k="outstanding" sort={sort} toggle={toggle} align="right" />
                <SortTh label="Status" k="status" sort={sort} toggle={toggle} />
                <SortTh label="" k={null} sort={sort} toggle={toggle} />
              </tr></thead>
              <tbody>{apply(arRows).map(r => (
                <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={td}>{r.invoice_number || "—"}{r.matter_type === "tpa" && <span className="badge" style={{ marginLeft: 6, fontSize: 9, background: "#fce7f3", color: "#9d174d" }}>TPA</span>}</td>
                  <td style={td}>{r.claim_number || "—"}</td><td style={{ ...td, color: "var(--text3)" }}>{r.due_date ? fmt.date(r.due_date) : "—"}</td>
                  <td style={{ ...td, textAlign: "right", color: r.days_overdue > 0 ? "#991b1b" : "var(--text3)", fontWeight: r.days_overdue > 60 ? 700 : 400 }}>{r.days_overdue > 0 ? r.days_overdue : "—"}</td>
                  <td style={{ ...td, textAlign: "right" }}><M v={r.invoice_total} /></td><td style={{ ...td, textAlign: "right", color: "var(--text3)" }}><M v={r.paid} /></td>
                  <td style={{ ...td, textAlign: "right" }}><M v={r.outstanding} b /></td>
                  <td style={td}><Pill s={r.status} /></td>
                  <td style={{ ...td, textAlign: "right" }}><button className="btn btn-outline btn-sm" style={{ padding: "3px 10px", fontSize: 11 }} onClick={() => navigate(openInvoiceLoc(r.id))}>Open</button></td></tr>))}</tbody></table></div>
          )}
        </div>
      </div>
    </div>
  );
}

// REVOPS-INVOICE-LIFECYCLE-1: prep-queue triage badges (flags computed server-side).
function PrepBadges({ p }) {
  const chip = (label, bg, fg) => <span key={label} className="badge" style={{ fontSize: 9, padding: "1px 6px", background: bg, color: fg, marginLeft: 4 }}>{label}</span>;
  return (
    <>
      {p.receipt_hold && chip("Receipt hold", "#fee2e2", "#991b1b")}
      {p.needs_client_ref && chip("Needs client ref", "#fef3c7", "#92400e")}
      {p.is_split && chip("Split", "#dbeafe", "#1e40af")}
      {p.has_instructions && chip("Instructions", "#e2e8f0", "#475569")}
      {p.dwell_days > 14 && chip(`${p.dwell_days}d`, "#fef3c7", "#92400e")}
    </>
  );
}

function PrepQueue({ onNavigateToClaim }) {
  const [data, setData] = useState({ packages: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => { setLoading(true); apiFetch("/invoice-prep").then(d => { setData(d); setErr(null); }).catch(e => setErr(String(e.message || e))).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);
  const { sort, toggle, apply } = useSort("submitted_at", "desc");
  const td = { padding: "8px 10px", fontSize: 13 };
  // Open a package full-page (REVOPS-INVOICE-LIFECYCLE-1: kills the bottom-expand UX-2 —
  // matches InvoiceList's full-surface swap so the detail is visible immediately).
  if (sel) return <PrepDetail pkgId={sel} onChanged={load} onClose={() => setSel(null)} onNavigateToClaim={onNavigateToClaim} />;
  return (
    <div>
      {err && <div className="card" style={{ marginBottom: 12 }}><div className="card-body" style={{ color: "var(--red)" }}>{err}</div></div>}
      <div className="card">
        <div className="section-header"><span className="section-title">Invoice Prep Queue ({(data.packages || []).length})</span></div>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? <Spinner label="Loading packages…" /> : (data.packages || []).length === 0 ?
            <div className="empty-state" style={{ padding: 24 }}>No packages submitted for prep. Lead Adjusters submit work from a claim's Billing Readiness tab.</div> : (
            <div style={stickyWrap}><table style={{ width: "100%", fontSize: 13 }}>
              <thead><tr>
                <SortTh label="Claim" k="claim_number" sort={sort} toggle={toggle} />
                <SortTh label="Lead Adjuster" k="lead_adjuster_name" sort={sort} toggle={toggle} />
                <SortTh label="Submitted" k="submitted_at" sort={sort} toggle={toggle} />
                <SortTh label="Service" k="service_fee_total" sort={sort} toggle={toggle} align="right" />
                <SortTh label="Expenses" k="expense_total" sort={sort} toggle={toggle} align="right" />
                <SortTh label="Instructions" k="billing_instructions" sort={sort} toggle={toggle} />
                <SortTh label="Status" k="status" sort={sort} toggle={toggle} />
                <SortTh label="" k={null} sort={sort} toggle={toggle} />
              </tr></thead>
              <tbody>{apply(data.packages).map(p => (
                <tr key={p.id} style={{ borderTop: "1px solid var(--border)", background: sel === p.id ? "var(--bg2,#f8fafc)" : "transparent" }}>
                  <td style={td}>{p.claim_number || `#${p.claim_id}`}<PrepBadges p={p} /></td><td style={td}>{p.lead_adjuster_name || "—"}</td>
                  <td style={{ ...td, color: "var(--text3)" }}>{fmt.date(p.submitted_at)}</td>
                  <td style={{ ...td, textAlign: "right" }}><M v={p.service_fee_total} /></td><td style={{ ...td, textAlign: "right" }}><M v={p.expense_total} /></td>
                  <td style={{ ...td, color: "var(--text3)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.billing_instructions || "—"}</td>
                  <td style={td}><span className="badge" style={{ fontSize: 10, background: "#e0e7ff", color: "#3730a3" }}>{p.status}</span></td>
                  <td style={{ ...td, textAlign: "right" }}><button className="btn btn-outline btn-sm" style={{ padding: "3px 10px", fontSize: 11 }} onClick={() => setSel(p.id)}>Review</button></td></tr>))}</tbody></table></div>
          )}
        </div>
      </div>
    </div>
  );
}

// H12 / LD-REV-014 — a real reason-bearing confirm for the two destructive-ish
// moves on prep entries. Replaces the bare one-click `write off` button (which
// sat one pixel from `remove` and permanently destroyed billable work with no
// confirm, no reason and no reverse anywhere in the product) and backs the new
// Restore control. Deliberately NOT a window.prompt: the review's cross-cutting
// finding is that prompts are doing the work of forms on money surfaces.
function ReasonModal({ title, blurb, cta, danger, busy, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  const ok = reason.trim().length >= 3;
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onCancel()}>
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-header"><h3>{title}</h3><button className="modal-close" onClick={onCancel}>×</button></div>
        <div className="modal-body">
          <div style={{ fontSize: 13, marginBottom: 10, color: "var(--text2)" }}>{blurb}</div>
          <div className="form-group full">
            <label>Reason (required)</label>
            <textarea autoFocus rows={3} value={reason} spellCheck={true}
                      placeholder="Why is this being done? Recorded on the entry and in the audit log."
                      onChange={e => setReason(e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className={danger ? "btn btn-danger" : "btn btn-primary"} disabled={!ok || busy}
                  title={ok ? undefined : "Enter a reason first."}
                  onClick={() => onConfirm(reason.trim())}>{busy ? "Working…" : cta}</button>
        </div>
      </div>
    </div>
  );
}

function PrepDetail({ pkgId, onChanged, onClose, onNavigateToClaim }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [mode, setMode] = useState("single");
  const [modeTouched, setModeTouched] = useState(false);
  // H12: {action, kind, id, label} — the pending write-off / restore awaiting a reason.
  const [confirm, setConfirm] = useState(null);
  const load = useCallback(() => apiFetch(`/invoice-prep/${pkgId}`).then(d => { setP(d); setErr(null); }).catch(e => setErr(String(e.message || e))), [pkgId]);
  useEffect(() => { load(); }, [load]);
  // INV-WS-1F: TPA matters default to slip-split (decision #4) unless the user picks otherwise.
  useEffect(() => { if (p && !modeTouched && p.claim_matter_type === "tpa") setMode("slip_split"); }, [p, modeTouched]);
  // Don't spin forever on a failed load — show the error + a way to close.
  if (!p) return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="card-body empty-state" style={err ? { color: "var(--red)" } : {}}>
        {err ? `Couldn't load this package — ${err}` : "Loading…"}
        {err && <div style={{ marginTop: 8 }}><button className="btn btn-outline btn-sm" onClick={onClose}>Close</button></div>}
      </div>
    </div>
  );
  const prepared = !!p.fee_invoice_id;
  // H11: mirror the BACKEND whitelist exactly (_PREP_ENTRY_EDITABLE_STATUSES).
  // The old check was `!prepared` only, which left an Issued package's buttons
  // hidden but its API reachable — and the API is the surface (Master SOP §4.6).
  const entriesEditable = p.status === "Ready" && !p.fee_invoice_id;
  const prepare = () => {
    setBusy(true); setErr(null);
    apiFetch(`/invoice-prep/${pkgId}/prepare`, { method: "POST", body: { allocation_mode: mode } })
      .then(inv => { window.alert(`Invoice ${inv.invoice_number} prepared — ${fmt.money(inv.invoice_total)} (service ${fmt.money(inv.service_fee_total)} + admin ${fmt.money(inv.admin_fee_total)} + exp ${fmt.money(inv.expense_total)}).`); onChanged && onChanged(); onClose(); })
      .catch(e => setErr(String(e.message || e))).finally(() => setBusy(false));
  };
  const editEntry = (action, kind, id, reason) => {
    setBusy(true);
    const body = { action, time_entry_ids: kind === "t" ? [id] : [], expense_entry_ids: kind === "e" ? [id] : [] };
    if (reason) body.reason = reason;
    apiFetch(`/invoice-prep/${pkgId}/entries`, { method: "POST", body })
      .then(d => { setP(d); setErr(null); setConfirm(null); onChanged && onChanged(); })
      .catch(e => setErr(String(e.message || e))).finally(() => setBusy(false));
  };
  const th = { padding: "6px 10px", textAlign: "left", fontSize: 11, color: "var(--text3)", textTransform: "uppercase" };
  const td = { padding: "6px 10px", fontSize: 13 };
  return (
    <div className="card" style={{ marginTop: 14, borderColor: "var(--navy)" }}>
      <div className="section-header">
        <span className="section-title">{p.claim_number} · {fmt.money((p.service_fee_total || 0) + (p.expense_total || 0))} · <span className="badge" style={{ fontSize: 10, background: "#e0e7ff", color: "#3730a3" }}>{p.status}</span></span>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {onNavigateToClaim && <button className="btn btn-outline btn-sm" onClick={() => onNavigateToClaim(p.claim_id)}>Open Claim</button>}
          <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button></div>
      </div>
      <div className="card-body">
        {err && <div style={{ color: "var(--red)", fontSize: 12, marginBottom: 8 }}>{err}</div>}
        {p.billing_instructions && <div style={{ fontSize: 13, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "8px 10px", marginBottom: 12 }}><b>Billing instructions:</b> {p.billing_instructions}</div>}
        <div style={{ display: "flex", gap: 24, fontSize: 13, marginBottom: 12 }}>
          <div>Service <M v={p.service_fee_total} b /></div><div>Expenses <M v={p.expense_total} b /></div>
          <div>Admin fee ({p.admin_fee_rate_pct != null ? p.admin_fee_rate_pct : 20}%) <M v={p.admin_fee_preview} b /></div>
          <div>Invoice total <M v={(Number(p.service_fee_total) || 0) + (Number(p.expense_total) || 0) + (Number(p.admin_fee_preview) || 0)} b /></div>
        </div>

        <div style={{ fontWeight: 600, fontSize: 12, margin: "6px 0" }}>Time ({(p.time_entries || []).length})</div>
        <table style={{ width: "100%", marginBottom: 10 }}><tbody>
          {(p.time_entries || []).map(e => (<tr key={e.id} style={{ borderTop: "1px solid var(--border)" }}>
            <td style={td}>{fmt.date(e.work_date)}</td><td style={{ ...td, whiteSpace: "nowrap", fontWeight: 600 }}>{e.user_name || "—"}</td><td style={{ ...td, color: "var(--text3)" }}>{e.description || "—"}</td><td style={{ ...td, textAlign: "right" }}><M v={e.amount} /></td>
            {entriesEditable && <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
              <button className="btn btn-outline btn-sm" style={{ padding: "1px 6px", fontSize: 10 }} disabled={busy} onClick={() => editEntry("remove", "t", e.id)}>remove</button>
              {" "}
              {/* H12: write-off now needs a reason + explicit confirm. */}
              <button className="btn btn-outline btn-sm" style={{ padding: "1px 6px", fontSize: 10, color: "#991b1b" }} disabled={busy}
                      title="Write this time off — it stops being billable. Reversible by an invoice-adjustment approver."
                      onClick={() => setConfirm({ action: "writeoff", kind: "t", id: e.id, amount: e.amount, who: e.user_name })}>write off</button>
            </td>}</tr>))}
        </tbody></table>
        <div style={{ fontWeight: 600, fontSize: 12, margin: "6px 0" }}>Expenses ({(p.expense_entries || []).length})</div>
        <table style={{ width: "100%", marginBottom: 14 }}><tbody>
          {(p.expense_entries || []).map(e => (<tr key={e.id} style={{ borderTop: "1px solid var(--border)" }}>
            <td style={td}>{fmt.date(e.expense_date)}</td><td style={td}>{e.expense_type || "—"}</td><td style={{ ...td, textAlign: "right" }}><M v={e.amount} /></td>
            {entriesEditable && <td style={{ ...td, textAlign: "right" }}><button className="btn btn-outline btn-sm" style={{ padding: "1px 6px", fontSize: 10 }} disabled={busy} onClick={() => editEntry("remove", "e", e.id)}>remove</button></td>}</tr>))}
        </tbody></table>

        {/* H12 / LD-REV-014: written-off time was invisible in the whole product.
            It is listed here with a Restore control for invoice-adjustment approvers. */}
        {(p.written_off_time_entries || []).length > 0 && (
          <div style={{ border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 10px", marginBottom: 14, background: "#f8fafc" }}>
            <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, color: "#475569" }}>
              Written off ({(p.written_off_time_entries || []).length}) — not billable, not on any invoice
            </div>
            <table style={{ width: "100%" }}><tbody>
              {p.written_off_time_entries.map(e => (<tr key={e.id} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={td}>{fmt.date(e.work_date)}</td>
                <td style={{ ...td, whiteSpace: "nowrap", fontWeight: 600 }}>{e.user_name || "—"}</td>
                <td style={{ ...td, color: "var(--text3)" }}>{e.description || "—"}</td>
                <td style={{ ...td, color: "var(--text3)", fontSize: 11 }}>
                  {e.writeoff_reason || (e.is_legacy_writeoff ? "(written off before reasons were recorded)" : "—")}
                  {e.writeoff_by_name ? ` · ${e.writeoff_by_name}` : ""}
                </td>
                <td style={{ ...td, textAlign: "right" }}><M v={e.amount} /></td>
                <td style={{ ...td, textAlign: "right" }}>
                  {can("approve_invoice_adjustment")
                    ? <button className="btn btn-outline btn-sm" style={{ padding: "1px 6px", fontSize: 10, color: "#166534" }} disabled={busy}
                              title="Restore this time to Unbilled so it can be billed on a future invoice."
                              onClick={() => setConfirm({ action: "unwriteoff", kind: "t", id: e.id, amount: e.amount, who: e.user_name })}>restore</button>
                    : <span style={{ fontSize: 10, color: "var(--text3)" }} title="Restoring a write-off needs invoice-adjustment approval.">🔒</span>}
                </td></tr>))}
            </tbody></table>
          </div>
        )}

        {p.prior_invoices && p.prior_invoices.length > 0 && <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 12 }}>{p.prior_invoices.length} prior invoice(s) on this claim.</div>}

        {entriesEditable && (
          <div style={{ display: "flex", gap: 10, alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Allocation:</label>
            <select value={mode} onChange={e => { setMode(e.target.value); setModeTouched(true); }} style={{ padding: "6px 9px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 6 }}>
              {Object.entries(ALLOC).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <div style={{ flex: 1 }} />
            <button className="btn btn-primary" onClick={prepare} disabled={busy}>Prepare Invoice</button>
          </div>
        )}
        {prepared && <div style={{ fontSize: 13, color: "#166534" }}>Prepared into invoice — see the Invoices tab.</div>}
      </div>
      {confirm && (
        <ReasonModal
          title={confirm.action === "writeoff" ? "Write off this time?" : "Restore written-off time?"}
          danger={confirm.action === "writeoff"}
          cta={confirm.action === "writeoff" ? "Write it off" : "Restore to Unbilled"}
          busy={busy}
          blurb={confirm.action === "writeoff"
            ? `${fmt.money(confirm.amount)} of ${confirm.who || "staff"} time stops being billable. It stays visible here and an invoice-adjustment approver can restore it.`
            : `${fmt.money(confirm.amount)} of ${confirm.who || "staff"} time goes back to Unbilled and can be billed on a future invoice.`}
          onCancel={() => setConfirm(null)}
          onConfirm={(reason) => editEntry(confirm.action, confirm.kind, confirm.id, reason)}
        />
      )}
    </div>
  );
}

const SELS = { padding: "6px 9px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 6, background: "#fff" };

function InvoiceList({ onNavigateToClaim }) {
  // REVOPS-INVOICE-LIFECYCLE-1: server-side search / filter / sort / pagination
  // (replaces the client-side 500-row dump). The list endpoint returns {invoices,total}.
  const [data, setData] = useState({ invoices: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const { navigate } = useNavigation();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [itype, setItype] = useState("");
  const [issued, setIssued] = useState("");
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;
  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ sort: sortKey, order: sortDir, limit: String(LIMIT), offset: String(offset) });
    if (q.trim()) p.set("q", q.trim());
    if (status) p.set("status", status);
    if (itype) p.set("invoice_type", itype);
    if (issued) p.set("issued", issued);
    apiFetch("/fee-invoices?" + p.toString())
      .then(d => { setData(d); setErr(null); }).catch(e => setErr(String(e.message || e))).finally(() => setLoading(false));
  }, [q, status, itype, issued, sortKey, sortDir, offset]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);   // debounce search
  useEffect(() => { setOffset(0); }, [q, status, itype, issued]);                                  // reset page on filter change
  const setSort = (k) => { if (sortKey === k) setSortDir(d => (d === "asc" ? "desc" : "asc")); else { setSortKey(k); setSortDir("asc"); } };
  const sortProp = { key: sortKey, dir: sortDir };
  const td = { padding: "8px 10px", fontSize: 13 };
  const invoices = data.invoices || [];
  const total = data.total || 0;
  // GLOBAL-NAV-2: opening an invoice navigates to its first-class location.
  return (
    <div>
      {err && <div className="card" style={{ marginBottom: 12 }}><div className="card-body" style={{ color: "var(--red)" }}>{err}</div></div>}
      <div className="card">
        <div className="section-header"><span className="section-title">Fee Invoices ({total})</span></div>
        {/* Filter / search bar */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", padding: "10px 14px", borderBottom: "1px solid var(--border)" }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search invoice #, claim #, insured, client…" style={{ ...SELS, flex: "1 1 280px", minWidth: 220 }} />
          <select value={status} onChange={e => setStatus(e.target.value)} style={SELS}>
            <option value="">All statuses</option>
            {["Draft", "Prepared", "Issued", "Sent", "Partially Paid", "Paid", "Disputed", "Written Off", "Void"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={itype} onChange={e => setItype(e.target.value)} style={SELS}>
            <option value="">All types</option>{["Interim", "Final", "Supplemental"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={issued} onChange={e => setIssued(e.target.value)} style={SELS}>
            <option value="">Issued + unissued</option><option value="false">Unissued (Draft/Prepared)</option><option value="true">Issued+</option>
          </select>
          {(q || status || itype || issued) && <button className="btn btn-outline btn-sm" onClick={() => { setQ(""); setStatus(""); setItype(""); setIssued(""); }}>Clear</button>}
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? <Spinner label="Loading invoices…" /> : invoices.length === 0 ? <div className="empty-state" style={{ padding: 24 }}>No invoices match.</div> : (
            <div style={stickyWrap}><table style={{ width: "100%", fontSize: 13 }}>
              <thead><tr>
                <SortTh label="Invoice #" k="invoice_number" sort={sortProp} toggle={setSort} />
                <SortTh label="Claim" k="claim_number" sort={sortProp} toggle={setSort} />
                <SortTh label="Date" k="date" sort={sortProp} toggle={setSort} />
                <SortTh label="Type" k="type" sort={sortProp} toggle={setSort} />
                <SortTh label="Service" k="service" sort={sortProp} toggle={setSort} align="right" />
                <SortTh label="Admin" k="admin" sort={sortProp} toggle={setSort} align="right" />
                <SortTh label="Total" k="amount" sort={sortProp} toggle={setSort} align="right" />
                <SortTh label="Status" k="status" sort={sortProp} toggle={setSort} />
                <SortTh label="" k={null} sort={sortProp} toggle={setSort} />
              </tr></thead>
              <tbody>{invoices.map(v => (
                <tr key={v.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={td}>{v.invoice_number || "—"}{v.is_historical && <span className="badge" style={{ marginLeft: 6, fontSize: 9, background: "#ede9fe", color: "#6d28d9" }}>VCA</span>}{v.matter_type === "tpa" && <span className="badge" style={{ marginLeft: 6, fontSize: 9, background: "#fce7f3", color: "#9d174d" }}>TPA</span>}</td>
                  <td style={td}>{v.claim_number || "—"}</td><td style={{ ...td, color: "var(--text3)" }}>{fmt.date(v.invoice_date)}</td><td style={td}>{v.invoice_type || "—"}</td>
                  <td style={{ ...td, textAlign: "right" }}><M v={v.service_fee_total} /></td>
                  <td style={{ ...td, textAlign: "right" }}>{v.admin_fee_total != null ? <M v={v.admin_fee_total} /> : "—"}</td>
                  <td style={{ ...td, textAlign: "right" }}><M v={v.invoice_total} b /></td>
                  <td style={td}><Pill s={v.status} /></td>
                  <td style={{ ...td, textAlign: "right" }}><button className="btn btn-outline btn-sm" style={{ padding: "3px 10px", fontSize: 11 }} onClick={() => navigate(openInvoiceLoc(v.id))}>Open</button></td></tr>))}</tbody></table></div>
          )}
        </div>
        {/* Pagination */}
        {total > LIMIT && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 14px", borderTop: "1px solid var(--border)", fontSize: 12, color: "var(--text3)" }}>
            <span>Showing {total === 0 ? 0 : offset + 1}–{Math.min(offset + LIMIT, total)} of {total}</span>
            <span style={{ display: "flex", gap: 6 }}>
              <button className="btn btn-outline btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>‹ Prev</button>
              <button className="btn btn-outline btn-sm" disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)}>Next ›</button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── INV-WS-1A — full-page Invoice Workspace (replaces the bottom-expand detail).
// LEFT = client-facing invoice preview; RIGHT = info tabs. Top bar keeps every
// existing lifecycle action unchanged. Live HTML/PDF parity is formalized in 1B;
// lifecycle (Issued/Disputed/Written-Off) + gates land in 1C.
const WS_TABS = [["general", "General"], ["allocation", "Allocation"], ["entries", "Time & Exp"],
  ["distribution", "Distribution"], ["payments", "Payments"], ["documents", "Documents"], ["history", "History"], ["qbo", "QBO"]];

export function InvoiceWorkspace({ invId, onChanged, onClose, onNavigateToClaim }) {
  const [inv, setInv] = useState(null);
  const [tab, setTab] = useState("general");
  const [editMeta, setEditMeta] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [emailing, setEmailing] = useState(false);   // GO_LIVE P3 / LD-INV-001: email-the-invoice modal
  const { reportLabel, recordVisit, navigate } = useNavigation();
  const load = useCallback(() => apiFetch(`/fee-invoices/${invId}`).then(d => { setInv(d); setErr(null); }).catch(e => setErr(String(e.message || e))), [invId]);
  useEffect(() => { load(); }, [load]);
  // GLOBAL-NAV-2: surface the invoice # for the breadcrumb + record the visit,
  // keyed to this invoice's own nav location.
  useEffect(() => {
    if (!inv?.id) return;
    const label = `Invoice ${inv.invoice_number || `#${invId}`}`;
    reportLabel(label, `invoice:${invId}`);
    recordVisit({ kind: "invoice", ref_id: String(invId), page: "invoice", label, params: { invId } });
  }, [inv?.id, inv?.invoice_number, invId, reportLabel, recordVisit]);
  const act = (path, body) => { setBusy(true); setErr(null); apiFetch(`/fee-invoices/${invId}/${path}`, { method: "POST", body: body || {} }).then(() => { load(); onChanged && onChanged(); }).catch(e => setErr(String(e.message || e))).finally(() => setBusy(false)); };
  const pay = () => { const a = window.prompt("Payment amount?"); if (a && Number(a) > 0) act("payments", { amount: Number(a) }); };
  const saveMeta = (patch) => { setBusy(true); setErr(null); apiFetch(`/fee-invoices/${invId}/metadata`, { method: "PATCH", body: patch }).then(() => { setEditMeta(false); load(); onChanged && onChanged(); }).catch(e => setErr(String(e.message || e))).finally(() => setBusy(false)); };
  const issue = () => { if (window.confirm("Issue this invoice? It finalizes + locks the invoice and recognizes the receivable. (It can no longer be unprepared — use Void to reverse.)")) act("issue"); };
  const dispute = () => { const r = window.prompt("Dispute reason? (client/underwriter is contesting this invoice)"); if (r && r.trim()) act("dispute", { reason: r.trim() }); };
  const resolve = () => { if (window.confirm("Clear the dispute and return the invoice to its live state?")) act("resolve-dispute"); };
  const writeoff = () => { const r = window.prompt("Write-off reason? This forgives the outstanding AR balance (the work stays billed). Requires approval rights."); if (r && r.trim()) act("write-off", { reason: r.trim() }); };
  // LD-INV-003: plain void of a partially-paid invoice 409s server-side; the escalated
  // force-void requires a second explicit confirmation and notifies accounting + the lead adjuster.
  const voidIt = () => {
    const r = window.prompt("Void reason?"); if (!r) return;
    const rel = window.confirm("Release the time/expense entries back to Unbilled (returned to the adjuster for later invoicing)? (OK = release, Cancel = keep billed)");
    setBusy(true); setErr(null);
    apiFetch(`/fee-invoices/${invId}/void`, { method: "POST", body: { reason: r, release_entries: rel } })
      .then(() => { load(); onChanged && onChanged(); })
      .catch(e => {
        let detail = String(e.message || e); try { detail = JSON.parse(e.message).detail || detail; } catch {}
        if (/force-void/i.test(detail)) {
          if (window.confirm(`${detail}\n\nFORCE-VOID this invoice anyway? Accounting and the lead adjuster will be notified.`)) {
            return apiFetch(`/fee-invoices/${invId}/void`, { method: "POST", body: { reason: r, release_entries: rel, force: true } })
              .then(() => { load(); onChanged && onChanged(); })
              .catch(e2 => setErr(String(e2.message || e2)));
          }
        } else setErr(detail);
      })
      .finally(() => setBusy(false));
  };
  // LD-INV-002: reissue a voided invoice — explicit typed number choice (same = amended issue N).
  const reissue = () => {
    const choice = window.prompt(
      'Reissue this voided invoice as a new Draft.\n\nType SAME to keep the same invoice number (marked "AMENDED — ISSUE N"),\nor NEW to assign the next invoice number.', "SAME");
    if (!choice) return;
    const mode = choice.trim().toUpperCase() === "NEW" ? "new" : choice.trim().toUpperCase() === "SAME" ? "same" : null;
    if (!mode) { setErr('Reissue cancelled — type SAME or NEW.'); return; }
    setBusy(true); setErr(null);
    apiFetch(`/fee-invoices/${invId}/reissue`, { method: "POST", body: { number_mode: mode } })
      .then(d => { onChanged && onChanged(); if (d.new_invoice_id) navigate(openInvoiceLoc(d.new_invoice_id)); })
      .catch(e => { let detail = String(e.message || e); try { detail = JSON.parse(e.message).detail || detail; } catch {} setErr(detail); })
      .finally(() => setBusy(false));
  };
  const unprepare = () => { if (!window.confirm("Unwind this invoice back to an editable prep package? The invoice is removed and its time/expense entries return to the prep queue for editing + re-preparing.")) return; setBusy(true); apiFetch(`/fee-invoices/${invId}/unprepare`, { method: "POST", body: {} }).then(() => { onChanged && onChanged(); onClose(); }).catch(e => setErr(String(e.message || e))).finally(() => setBusy(false)); };
  const pdf = () => { setBusy(true); downloadReimbExport(`/fee-invoices/${invId}/pdf`).catch(e => setErr(String(e.message || e))).finally(() => setBusy(false)); };
  // Issue + download: mark Sent + record distribution (NO email transmitted) AND download the PDF.
  const issueAndDownload = (method) => {
    setBusy(true); setErr(null);
    apiFetch(`/fee-invoices/${invId}/distribute`, { method: "POST", body: { method } })
      .then(() => downloadReimbExport(`/fee-invoices/${invId}/pdf`))
      .then(() => { load(); onChanged && onChanged(); })
      .catch(e => setErr(String(e.message || e))).finally(() => setBusy(false));
  };
  // Don't spin forever if the fetch errored (e.g. a 404/500) — surface it + a way back.
  if (!inv) return (
    <div>
      <button className="btn btn-outline btn-sm" onClick={onClose}>← Back to Invoices</button>
      <div className="card" style={{ marginTop: 12 }}>
        <div className="card-body empty-state" style={err ? { color: "var(--red)" } : {}}>
          {err ? `Couldn't load this invoice — ${err}` : "Loading…"}
        </div>
      </div>
    </div>
  );
  const hist = inv.is_historical;
  const s = inv.status;
  const canApprove = !!inv.can_approve_adjustment;          // second-approver rights (INV-WS-1C)
  const isPreIssue = s === "Draft" || s === "Prepared";
  const isLive = s === "Issued" || s === "Sent" || s === "Partially Paid";   // active receivable
  const canPay = !hist && isLive;
  const canDistribute = !hist && isLive;
  const canDispute = !hist && isLive;
  const canWriteoff = !hist && canApprove && (isLive || s === "Disputed");
  // A VCA-billed invoice is otherwise read-only here; Void is how a person rules on one VCA no longer has.
  const canVoid = hist ? (s === "Sent" && canApprove) : (s !== "Void" && s !== "Written Off" && (isPreIssue || canApprove));
  const canEmail = !hist && (isPreIssue || isLive);           // LD-INV-001 (pre-issue send auto-issues)
  const canReissue = !hist && s === "Void";                   // LD-INV-002
  const th = { padding: "6px 10px", textAlign: "left", fontSize: 11, color: "var(--text3)", textTransform: "uppercase" };
  const td = { padding: "6px 10px", fontSize: 13 };
  return (
    <div>
      {/* Top action bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <button className="btn btn-outline btn-sm" onClick={onClose}>← Back to Invoices</button>
        <span style={{ fontWeight: 700, fontSize: 15 }}>Invoice {inv.invoice_number || "(draft)"}</span>
        <Pill s={inv.status} />
        {(inv.issue_no || 1) > 1 && <span className="badge" style={{ fontSize: 9, background: "#fef3c7", color: "#92400e" }}>AMENDED · ISSUE {inv.issue_no}</span>}
        {hist && <span className="badge" style={{ fontSize: 9, background: "#ede9fe", color: "#6d28d9" }}>VCA</span>}
        {inv.matter_type === "tpa" && <span className="badge" style={{ fontSize: 9, background: "#fce7f3", color: "#9d174d" }}>TPA</span>}
        <div style={{ flex: 1 }} />
        <button className="btn btn-outline btn-sm" onClick={pdf} disabled={busy} title="Download printable invoice PDF">Print PDF</button>
        {canEmail && <button className="btn btn-primary btn-sm" onClick={() => setEmailing(true)} disabled={busy} title={isPreIssue ? "Email the invoice PDF — sending issues + locks the invoice and marks it Sent" : "Email the invoice PDF to the client"}>Email Invoice…</button>}
        {!hist && isPreIssue && <button className="btn btn-outline btn-sm" onClick={issue} disabled={busy} title="Finalize + lock the invoice and recognize the receivable (distinct from delivery)">Issue Invoice</button>}
        {canDistribute && <button className="btn btn-outline btn-sm" onClick={() => issueAndDownload("client")} disabled={busy} title="Record client delivery, mark Sent, and download the PDF to email yourself outside Atticus">Send + Download (Client)</button>}
        {canDistribute && <button className="btn btn-outline btn-sm" onClick={() => issueAndDownload("adjuster")} disabled={busy} title="Record adjuster delivery, mark Sent, and download the PDF (no email is auto-transmitted)">Send + Download (Adjuster)</button>}
        {!hist && s === "Issued" && <button className="btn btn-outline btn-sm" onClick={() => act("send")} disabled={busy} title="Mark delivered (Sent) — status only, no email transmitted">Mark Sent</button>}
        {!hist && s === "Sent" && <button className="btn btn-outline btn-sm" onClick={() => { if (window.confirm("Un-send this invoice? The Sent + Issued flags are revoked and it returns to Prepared, so it can be unprepared and edited. Any email already delivered is NOT recalled (the distribution history is kept). Blocked if a payment was recorded or the invoice already pushed to QBO.")) act("unsend"); }} disabled={busy} title="Revoke Sent + Issued — back to Prepared so it can be unprepared/edited">Un-send…</button>}
        {canReissue && <button className="btn btn-primary btn-sm" onClick={reissue} disabled={busy} title="Clone this voided invoice as a new Draft — same number (amended issue) or the next number">Reissue…</button>}
        {canPay && <button className="btn btn-blue btn-sm" onClick={pay} disabled={busy}>Record Payment</button>}
        {canDispute && <button className="btn btn-outline btn-sm" onClick={dispute} disabled={busy} title="Mark the invoice disputed by the client/underwriter">Dispute</button>}
        {!hist && s === "Disputed" && <button className="btn btn-primary btn-sm" onClick={resolve} disabled={busy}>Resolve Dispute</button>}
        {canWriteoff && <button className="btn btn-outline btn-sm" onClick={writeoff} disabled={busy} style={{ color: "#9a3412", borderColor: "#fed7aa" }} title="Forgive the AR balance (requires approval rights)">Write Off</button>}
        {!hist && isPreIssue && <button className="btn btn-outline btn-sm" onClick={unprepare} disabled={busy} title="Unwind to editable prep package">Unprepare</button>}
        {canVoid && <button className="btn btn-outline btn-sm" onClick={voidIt} disabled={busy} style={{ color: "#991b1b", borderColor: "#fecaca" }} title={isPreIssue ? "Void this draft" : "Void an issued invoice (requires approval rights)"}>Void</button>}
      </div>
      {err && <div className="card" style={{ marginBottom: 12 }}><div className="card-body" style={{ color: "var(--red)" }}>{err}</div></div>}

      {/* Two-column workspace */}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        {/* LEFT — client-facing preview (INV-WS-1B: the real generated PDF, embedded) */}
        <div style={{ flex: "1.05 1 0", minWidth: 360 }}>
          <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", marginBottom: 6 }}>Client-facing preview · the official PDF{isPreIssue ? " (DRAFT)" : ""}</div>
          <InvoicePdfPreview invId={invId} version={inv.updated_at} />
        </div>
        {/* RIGHT — info tabs */}
        <div style={{ flex: "0.95 1 0", minWidth: 320 }}>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
            {WS_TABS.map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} style={{ padding: "4px 11px", fontSize: 12, fontWeight: 600, borderRadius: 14, cursor: "pointer",
                border: "1px solid " + (tab === k ? "var(--navy)" : "var(--border)"), background: tab === k ? "var(--navy)" : "transparent", color: tab === k ? "#fff" : "var(--text2)" }}>{l}</button>
            ))}
          </div>
          <div className="card"><div className="card-body">
            {tab === "general" && (editMeta ? <MetadataForm inv={inv} onSave={saveMeta} onCancel={() => setEditMeta(false)} busy={busy} /> : (
              <table style={{ width: "100%", fontSize: 13 }}><tbody>
                <WsRow label="Claim" value={onNavigateToClaim ? <button className="btn btn-link btn-sm" style={{ padding: 0 }} onClick={() => onNavigateToClaim(inv.claim_id)}>{inv.claim_number}</button> : inv.claim_number} />
                <WsRow label="Matter type" value={(inv.matter_type || inv.claim_matter_type || "ia").toUpperCase()} />
                <WsRow label="Invoice type" value={inv.invoice_type || "Interim"} />
                <WsRow label="Invoice date" value={fmt.date(inv.invoice_date)} />
                <WsRow label="Terms" value={inv.terms || "Net 30"} />
                <WsRow label="Due date" value={inv.due_date ? fmt.date(inv.due_date) : "—"} />
                {inv.purchase_order && <WsRow label="PO #" value={inv.purchase_order} />}
                {inv.client_reference_number && <WsRow label="Client reference #" value={inv.client_reference_number} />}
                <WsRow label="Status" value={<Pill s={inv.status} />} />
                <WsRow label="Allocation" value={ALLOC[inv.allocation_mode] || inv.allocation_mode || "—"} />
                <WsRow label="Created by" value={inv.created_by_name || "—"} />
                {inv.prepared_by_name && <WsRow label="Prepared by" value={inv.prepared_by_name} />}
                {inv.sent_by_name && <WsRow label="Sent by" value={inv.sent_by_name} />}
                {inv.cover_note && <WsRow label="Cover note" value={inv.cover_note} />}
                {inv.billing_instructions_snapshot && <WsRow label="Billing instructions" value={inv.billing_instructions_snapshot} />}
                {inv.void_reason && <WsRow label="Voided" value={<span style={{ color: "#991b1b" }}>{inv.void_reason}</span>} />}
                {!hist && isPreIssue && <tr><td colSpan={2} style={{ paddingTop: 10 }}><button className="btn btn-outline btn-sm" onClick={() => setEditMeta(true)}>Edit invoice details / Bill-To / notes</button></td></tr>}
              </tbody></table>
            ))}
            {tab === "documents" && (
              /* DC-4b (2026-06-17): invoice support docs (the invoice PDF is the
                 client-facing preview on the left; this is for backup/attachments). */
              <EntityDocuments entityType="invoice" entityId={invId} canEdit={can("manage_invoicing")} defaultPhi={false} title="Invoice documents" />
            )}
            {tab === "allocation" && (
              <div>
                <div style={{ fontSize: 13, marginBottom: 8 }}>Mode: <b>{ALLOC[inv.allocation_mode] || inv.allocation_mode || "—"}</b></div>
                {(inv.shares || []).length > 0 ? (
                  <table style={{ width: "100%" }}><thead><tr><th style={th}>Underwriter</th><th style={{ ...th, textAlign: "right" }}>%</th><th style={{ ...th, textAlign: "right" }}>Share</th><th style={th}>Payment</th></tr></thead>
                    <tbody>{inv.shares.map(s => (<tr key={s.id} style={{ borderTop: "1px solid var(--border)" }}>
                      <td style={td}>{s.display_name}{s.is_lead && <span className="badge" style={{ marginLeft: 6, fontSize: 9, background: "#fee2e2", color: "#991b1b" }}>LEAD</span>}</td>
                      <td style={{ ...td, textAlign: "right" }}>{s.participation_pct != null ? Number(s.participation_pct).toFixed(1) + "%" : "—"}</td>
                      <td style={{ ...td, textAlign: "right" }}><M v={s.share_total} /></td>
                      <td style={td}><span className="badge" style={{ fontSize: 10, background: s.payment_status === "Paid" ? "#dcfce7" : "#f1f5f9", color: s.payment_status === "Paid" ? "#166534" : "#475569" }}>{s.payment_status}</span></td></tr>))}</tbody></table>
                ) : <div className="empty-state" style={{ padding: 16 }}>Single / merged invoice — no underwriter split.</div>}
              </div>
            )}
            {tab === "entries" && (
              <div>
                <div style={{ fontWeight: 600, fontSize: 12, margin: "2px 0 4px" }}>Time ({(inv.time_entries || []).length})</div>
                <table style={{ width: "100%", marginBottom: 12 }}><tbody>
                  {(inv.time_entries || []).map(e => (<tr key={e.id} style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={td}>{fmt.date(e.work_date)}</td><td style={{ ...td, whiteSpace: "nowrap", fontWeight: 600 }}>{e.user_name || "—"}</td><td style={{ ...td, color: "var(--text3)" }}>{e.description || "—"}</td><td style={{ ...td, textAlign: "right" }}><M v={e.amount} /></td></tr>))}
                  {(inv.time_entries || []).length === 0 && <tr><td style={{ ...td, color: "var(--text3)" }} colSpan={4}>No time entries.</td></tr>}
                </tbody></table>
                <div style={{ fontWeight: 600, fontSize: 12, margin: "2px 0 4px" }}>Expenses ({(inv.expense_entries || []).length})</div>
                <table style={{ width: "100%" }}><tbody>
                  {(inv.expense_entries || []).map(e => (<tr key={e.id} style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={td}>{fmt.date(e.expense_date)}</td><td style={td}>{e.expense_type || "—"}</td><td style={{ ...td, textAlign: "right" }}><M v={e.amount} /></td></tr>))}
                  {(inv.expense_entries || []).length === 0 && <tr><td style={{ ...td, color: "var(--text3)" }} colSpan={3}>No expense entries.</td></tr>}
                </tbody></table>
              </div>
            )}
            {tab === "distribution" && (
              <div style={{ fontSize: 13 }}>
                {canEmail ? (
                  <div style={{ marginBottom: 10 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => setEmailing(true)} disabled={busy}>Email Invoice…</button>
                    <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 6 }}>
                      Sends the invoice PDF from your connected Outlook{isPreIssue ? ", issues + locks the invoice," : ""} and marks it Sent. The exact PDF sent is filed on the claim.</div>
                  </div>
                ) : <div style={{ color: "var(--text3)", marginBottom: 10 }}>{hist ? "Historical invoice — distribution not tracked." : `Invoice is ${s}; no further distribution action.`}</div>}
                {canDistribute && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button className="btn btn-outline btn-sm" onClick={() => issueAndDownload("client")} disabled={busy} title="Record delivery + download the PDF to send outside Atticus">Send + Download (Client)</button>
                    <button className="btn btn-outline btn-sm" onClick={() => issueAndDownload("adjuster")} disabled={busy}>Send + Download (Adjuster)</button>
                    {s === "Issued" && <button className="btn btn-outline btn-sm" onClick={() => act("send")} disabled={busy}>Mark Sent (no download)</button>}
                  </div>
                )}
                {(inv.distributions || []).length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>Distribution history</div>
                    <table style={{ width: "100%" }}><tbody>
                      {inv.distributions.map(d => (<tr key={d.id} style={{ borderTop: "1px solid var(--border)" }}>
                        <td style={{ padding: "5px 8px", fontSize: 12, whiteSpace: "nowrap" }}>{fmt.date(d.distributed_at)}</td>
                        <td style={{ padding: "5px 8px", fontSize: 12, textTransform: "capitalize" }}>{d.method === "email" ? "📧 email" : d.method}</td>
                        <td style={{ padding: "5px 8px", fontSize: 12, color: "var(--text2)", wordBreak: "break-all" }}>{d.recipient || "—"}</td>
                        <td style={{ padding: "5px 8px", fontSize: 12, color: "var(--text3)", whiteSpace: "nowrap" }}>{d.distributed_by_name || "—"}</td></tr>))}
                    </tbody></table>
                  </div>
                )}
              </div>
            )}
            {tab === "payments" && (
              <div>
                {(inv.payments || []).length > 0 ? (
                  <table style={{ width: "100%", marginBottom: 10 }}><thead><tr><th style={th}>Date</th><th style={th}>Method</th><th style={th}>Reference</th><th style={{ ...th, textAlign: "right" }}>Amount</th></tr></thead>
                    <tbody>{inv.payments.map(p => (<tr key={p.id} style={{ borderTop: "1px solid var(--border)" }}>
                      <td style={td}>{fmt.date(p.payment_date)}</td><td style={td}>{p.method || "—"}</td><td style={td}>{p.reference || "—"}</td><td style={{ ...td, textAlign: "right" }}><M v={p.amount} /></td></tr>))}</tbody></table>
                ) : <div className="empty-state" style={{ padding: 16 }}>No payments recorded.</div>}
                {canPay && <button className="btn btn-blue btn-sm" onClick={pay} disabled={busy}>Record Payment</button>}
              </div>
            )}
            {tab === "history" && (
              <table style={{ width: "100%", fontSize: 13 }}><tbody>
                <WsRow label="Created" value={`${inv.created_by_name || "—"}${inv.created_at ? " · " + fmt.date(inv.created_at) : ""}`} />
                {inv.prepared_by_name && <WsRow label="Prepared" value={inv.prepared_by_name} />}
                {inv.issued_at && <WsRow label="Issued" value={fmt.date(inv.issued_at)} />}
                {inv.sent_by_name && <WsRow label="Sent" value={`${inv.sent_by_name}${inv.sent_at ? " · " + fmt.date(inv.sent_at) : ""}`} />}
                {inv.disputed_at && <WsRow label="Disputed" value={<span style={{ color: "#9a3412" }}>{fmt.date(inv.disputed_at)}{inv.dispute_reason ? " · " + inv.dispute_reason : ""}</span>} />}
                {inv.written_off_at && <WsRow label="Written off" value={<span style={{ color: "#475569" }}>{fmt.date(inv.written_off_at)}{inv.writeoff_reason ? " · " + inv.writeoff_reason : ""}</span>} />}
                {inv.void_reason && <WsRow label="Voided" value={<span style={{ color: "#991b1b" }}>{inv.void_reason}</span>} />}
                {inv.reissued_from_id && <WsRow label="Reissued from" value={<button className="btn btn-link btn-sm" style={{ padding: 0 }} onClick={() => navigate(openInvoiceLoc(inv.reissued_from_id))}>Voided invoice #{inv.reissued_from_id}</button>} />}
                {(inv.issue_no || 1) > 1 && <WsRow label="Issue" value={`Amended — issue ${inv.issue_no} of invoice ${inv.invoice_number}`} />}
              </tbody></table>
            )}
            {tab === "qbo" && (
              <div style={{ fontSize: 13 }}>
                {inv.qbo_invoice_id ? (
                  <table style={{ width: "100%" }}><tbody>
                    <WsRow label="QBO invoice" value={`${inv.qbo_invoice_number || ""} (id ${inv.qbo_invoice_id})`} />
                    <WsRow label="Sync status" value={inv.qbo_sync_status || "pushed"} />
                    {inv.qbo_last_sync && <WsRow label="Last sync" value={fmt.date(inv.qbo_last_sync)} />}
                  </tbody></table>
                ) : inv.qbo_sync_status === "queued" || inv.qbo_sync_status === "error" ? (
                  <div>
                    <table style={{ width: "100%", marginBottom: 10 }}><tbody>
                      <WsRow label="Sync status" value={<span style={{ color: inv.qbo_sync_status === "error" ? "#991b1b" : "#92400e", fontWeight: 600 }}>{inv.qbo_sync_status === "error" ? "Error — retry" : "Queued for QBO Operating"}</span>} />
                      {inv.qbo_queued_at && <WsRow label="Queued" value={fmt.date(inv.qbo_queued_at)} />}
                    </tbody></table>
                    <button className="btn btn-blue btn-sm" disabled={busy} onClick={() => {
                      setBusy(true); setErr(null);
                      apiFetch(`/fee-invoices/${invId}/qbo-push`, { method: "POST", body: {} })
                        .then(() => { load(); onChanged && onChanged(); })
                        .catch(e => { let d = String(e.message || e); try { d = JSON.parse(e.message).detail || d; } catch {} setErr(d); })
                        .finally(() => setBusy(false));
                    }}>Push to QBO Operating</button>
                    <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 6 }}>
                      Creates/finds the client Customer and the claim sub-customer, then posts the invoice.
                      Unavailable (503) until Operating credentials are seeded.</div>
                  </div>
                ) : <div className="empty-state" style={{ padding: 16 }}>Not queued for QuickBooks — the invoice queues for the QBO Operating push when it is Sent.</div>}
              </div>
            )}
          </div></div>
        </div>
      </div>
      {emailing && <InvoiceEmailModal inv={inv} onClose={() => setEmailing(false)}
        onSent={() => { setEmailing(false); load(); onChanged && onChanged(); }} />}
    </div>
  );
}

// ── GO_LIVE P3 / LD-INV-001 — Email-the-invoice modal. Modeled on ComposeEmailModal
// (same Outlook preflight + no-silent-failure), but posts to the dedicated
// /fee-invoices/{id}/email endpoint which atomically renders + attaches the invoice
// PDF, transitions to Sent, stamps distribution history, and files the exact PDF sent.
// Prefills recipients from the invoice Bill-To snapshot, falling back to the client's
// billing recipient profile (INV-WS-1G).
function InvoiceEmailModal({ inv, onClose, onSent }) {
  const bt = inv.bill_to_snapshot || {};
  const [status, setStatus] = useState(null);
  const [to, setTo] = useState(bt.Email || "");
  const [cc, setCc] = useState(bt.CC || "");
  const [subject, setSubject] = useState(
    `Walters Northwest Invoice ${inv.invoice_number || `#${inv.id}`}${inv.claim_number ? ` — Claim ${inv.claim_number}` : ""}`);
  const [body, setBody] = useState(
    `Please find attached invoice ${inv.invoice_number || `#${inv.id}`}${inv.claim_number ? ` for claim ${inv.claim_number}` : ""} in the amount of ${fmt.money(inv.invoice_total)}. Terms: ${inv.terms || "Net 30"}.\n\nThank you,`);
  const [identity, setIdentity] = useState("self");
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);
  const [sentInfo, setSentInfo] = useState(null);  // set on success → confirmation view
  const [profileHint, setProfileHint] = useState(null);
  useEffect(() => {
    apiFetch("/me/outlook/status").then(setStatus).catch(() => setStatus({ connected: false }));
  }, []);
  // Prefill from the client's saved billing recipient profile when the snapshot has no email.
  useEffect(() => {
    if (bt.Email || !inv.bill_to_company_id) return;
    apiFetch(`/billing-recipient-profiles?company_id=${inv.bill_to_company_id}`).then(d => {
      const p = (d.profiles || []).find(x => x.email);
      if (p) { setTo(t => t || p.email || ""); setCc(c => c || p.cc_email || ""); setProfileHint(p.label); }
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);
  const ready = status && status.connected && status.email_send_enabled;
  const isPreIssue = inv.status === "Draft" || inv.status === "Prepared";
  const send = () => {
    if (!to.trim()) { setMsg({ type: "error", text: "Add at least one recipient." }); return; }
    setSending(true); setMsg(null);
    const esc = (x) => x.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    apiFetch(`/fee-invoices/${inv.id}/email`, { method: "POST", body: {
      to, cc, subject, body_html: esc(body).replace(/\n/g, "<br>"), sending_identity: identity } })
      .then(() => {
        setSentInfo({
          from: identity === "self" ? (status?.email || "my address") : (IDENTITY_LABEL[identity] || identity),
          to, cc, subject, body,
          attachments: [{ name: `Invoice ${inv.invoice_number || `#${inv.id}`} PDF (generated at send, exact copy filed on the claim)` }],
          claimNumber: inv.claim_number || null,
        });
        setSending(false);
      })
      .catch(e => { let d = "Email could not be sent."; try { d = JSON.parse(e.message).detail || d; } catch {} setMsg({ type: "error", text: d }); setSending(false); });
  };
  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "48px 16px", overflowY: "auto" };
  const sheet = { background: "var(--surface2,#fff)", borderRadius: 12, width: "100%", maxWidth: 620, boxShadow: "0 10px 40px rgba(0,0,0,0.25)" };
  const lbl = { fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3, display: "block" };
  const inp = { width: "100%", boxSizing: "border-box" };
  const IDENTITY_LABEL = { self: "My address", accounting: "accounting@waltersnorthwest.com", admin: "adminsupport@waltersnorthwest.com" };
  if (sentInfo) {
    const finish = () => { onSent && onSent(); };
    return (
      <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) finish(); }}>
        <div style={sheet}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Invoice sent · marked Sent</h3>
            <button className="btn btn-outline btn-sm" onClick={finish}>✕</button>
          </div>
          <EmailSentConfirmation info={sentInfo} onDone={finish} />
        </div>
      </div>
    );
  }
  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={sheet}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Email Invoice {inv.invoice_number || `#${inv.id}`}</h3>
          <button className="btn btn-outline btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: 18 }}>
          {status && !status.connected && <div className="alert-error" style={{ marginBottom: 12 }}>Your Outlook isn't connected. Connect it in Profile → Outlook email before sending.</div>}
          {status && status.connected && !status.email_send_enabled && <div className="alert-error" style={{ marginBottom: 12 }}>Email sending isn't enabled for your account. Ask an administrator to enable it.</div>}
          {msg && <div className={msg.type === "error" ? "alert-error" : ""} style={msg.type === "success" ? { background: "#dcfce7", color: "#166534", padding: "8px 12px", borderRadius: 6, marginBottom: 12 } : { marginBottom: 12 }}>{msg.text}</div>}
          <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "8px 10px", marginBottom: 12, fontSize: 12 }}>
            Sending {isPreIssue ? "will issue + lock this invoice and mark it " : "marks the invoice "}<b>Sent</b>. The generated invoice PDF is attached and the exact copy sent is filed on the claim.</div>
          <div style={{ marginBottom: 10 }}>
            <label style={lbl}>From</label>
            <select value={identity} onChange={e => setIdentity(e.target.value)} style={inp} disabled={!ready}>
              {["self", ...(status?.shared_identities || [])].filter((v, i, a) => a.indexOf(v) === i).map(k => (
                <option key={k} value={k}>{IDENTITY_LABEL[k] || k}{k === "self" && status?.email ? ` (${status.email})` : ""}</option>))}
            </select>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={lbl}>To{profileHint ? <span style={{ textTransform: "none", letterSpacing: 0 }}> · prefilled from profile "{profileHint}"</span> : null}</label>
            <input value={to} onChange={e => setTo(e.target.value)} placeholder="ap@client.com, billing@client.com" style={inp} disabled={!ready} />
          </div>
          <div style={{ marginBottom: 10 }}><label style={lbl}>Cc</label><input value={cc} onChange={e => setCc(e.target.value)} style={inp} disabled={!ready} /></div>
          <div style={{ marginBottom: 10 }}><label style={lbl}>Subject</label><input value={subject} onChange={e => setSubject(e.target.value)} style={inp} disabled={!ready} /></div>
          <div style={{ marginBottom: 10 }}><label style={lbl}>Message</label><textarea value={body} onChange={e => setBody(e.target.value)} rows={7} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} disabled={!ready} /></div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, background: "var(--bg3,#f8fafc)", padding: "6px 10px", borderRadius: 6 }}>
            <span>📄</span><span style={{ flex: 1 }}>Invoice {inv.invoice_number || `#${inv.id}`} PDF <span style={{ color: "var(--text3)" }}>(generated at send · exact copy filed on the claim)</span></span>
          </div>
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 8 }}>A reference table (invoice facts + payor split) is included in the body, then your signature. The email is filed on the claim (PHI-audited).</div>
        </div>
        <div style={{ padding: "12px 18px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={send} disabled={sending || !ready}>{sending ? "Sending…" : `Send + Mark Sent`}</button>
        </div>
      </div>
    </div>
  );
}

// REVOPS-INVOICE-LIFECYCLE-1 Phase C: full invoice-metadata editor — every field the
// metadata PATCH accepts (invoice type, terms/due/PO, client ref, full Bill-To,
// cover note, remittance, internal notes) + reusable recipient profiles (INV-WS-1G).
function MetadataForm({ inv, onSave, onCancel, busy }) {
  const bt0 = inv.bill_to_snapshot || {};
  const [invType, setInvType] = useState(inv.invoice_type || "Interim");
  const [invDate, setInvDate] = useState(inv.invoice_date ? String(inv.invoice_date).slice(0, 10) : "");   // §6B
  const [terms, setTerms] = useState(inv.terms || "Net 30");
  const [due, setDue] = useState(inv.due_date ? String(inv.due_date).slice(0, 10) : "");
  const [chargesThrough, setChargesThrough] = useState(inv.charges_through_date ? String(inv.charges_through_date).slice(0, 10) : "");
  const [issueDate, setIssueDate] = useState(inv.issue_date ? String(inv.issue_date).slice(0, 10) : "");
  const [po, setPo] = useState(inv.purchase_order || "");
  const [clientRef, setClientRef] = useState(inv.client_reference_number || "");
  const [note, setNote] = useState(inv.cover_note || "");
  const [remit, setRemit] = useState(inv.remittance_instructions || "");
  const [internal, setInternal] = useState(inv.internal_invoice_notes || "");
  const [billTo, setBillTo] = useState(bt0.InvoiceTo || "");
  const [attn, setAttn] = useState(bt0.Att || "");
  const [address, setAddress] = useState(bt0.Address || "");
  const [city, setCity] = useState(bt0.City || "");
  const [statev, setStatev] = useState(bt0.State || "");
  const [zip, setZip] = useState(bt0.Zip || "");
  const [email, setEmail] = useState(bt0.Email || "");
  const [cc, setCc] = useState(bt0.CC || "");
  const [profiles, setProfiles] = useState([]);
  const [profSel, setProfSel] = useState("");
  const [profMsg, setProfMsg] = useState("");
  useEffect(() => { apiFetch("/billing-recipient-profiles").then(d => setProfiles(d.profiles || [])).catch(() => {}); }, []);
  const applyProfile = () => {
    const p = profiles.find(x => String(x.id) === String(profSel));
    if (!p) return;
    setBillTo(p.invoice_to || ""); setAttn(p.attn || ""); setAddress(p.address || "");
    setCity(p.city || ""); setStatev(p.state || ""); setZip(p.zip || ""); setEmail(p.email || ""); setCc(p.cc_email || "");
    if (p.default_terms) setTerms(p.default_terms);
    if (p.remittance_instructions) setRemit(p.remittance_instructions);
    setProfMsg(`Applied "${p.label}" — review the fields below, then Save.`);
  };
  const saveAsProfile = () => {
    const label = window.prompt("Save the current Bill-To + terms + remittance as a reusable profile. Label?", billTo || "");
    if (!label || !label.trim()) return;
    apiFetch("/billing-recipient-profiles", { method: "POST", body: {
      company_id: inv.bill_to_company_id || null, label: label.trim(), invoice_to: billTo, attn, address,
      city, state: statev, zip, email, cc_email: cc, default_terms: terms, remittance_instructions: remit } })
      .then(np => { setProfiles(ps => [...ps, np]); setProfSel(String(np.id)); setProfMsg(`Saved profile "${np.label}".`); })
      .catch(e => setProfMsg(String(e.message || e)));
  };
  const save = () => onSave({
    invoice_type: invType, invoice_date: invDate || null, terms, due_date: due || null, charges_through_date: chargesThrough || null, issue_date: issueDate || null, purchase_order: po,
    client_reference_number: clientRef, cover_note: note, remittance_instructions: remit, internal_invoice_notes: internal,
    bill_to: { InvoiceTo: billTo, Att: attn, Address: address, City: city, State: statev, Zip: zip, Email: email, CC: cc },
  });
  const lbl = { fontSize: 11, color: "var(--text3)", textTransform: "uppercase", display: "block", marginBottom: 3 };
  const inp = { width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 6, boxSizing: "border-box" };
  const sec = { fontSize: 11, fontWeight: 700, color: "var(--navy)", textTransform: "uppercase", letterSpacing: 0.4, marginTop: 6, borderBottom: "1px solid var(--border)", paddingBottom: 3 };
  const F = (label, node) => <div><label style={lbl}>{label}</label>{node}</div>;
  const T = (val, set, ph) => <input value={val} onChange={e => set(e.target.value)} style={inp} placeholder={ph || "optional"} />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      {/* Recipient profile picker (INV-WS-1G) */}
      <div style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
        <div style={{ flex: 1 }}>{F("Recipient profile", (
          <select value={profSel} onChange={e => setProfSel(e.target.value)} style={inp}>
            <option value="">— choose a saved profile —</option>
            {profiles.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>))}</div>
        <button className="btn btn-outline btn-sm" disabled={!profSel} onClick={applyProfile}>Apply</button>
        <button className="btn btn-outline btn-sm" onClick={saveAsProfile} title="Save current Bill-To as a reusable profile">Save as…</button>
      </div>
      {profMsg && <div style={{ fontSize: 11, color: "var(--text3)" }}>{profMsg}</div>}

      <div style={sec}>Invoice details</div>
      {F("Invoice type", <select value={invType} onChange={e => setInvType(e.target.value)} style={inp}>{["Interim", "Final", "Supplemental"].map(t => <option key={t} value={t}>{t}</option>)}</select>)}
      {F("Invoice date", <input type="date" value={invDate} onChange={e => setInvDate(e.target.value)} style={inp} />)}
      {F("Terms", <select value={terms} onChange={e => setTerms(e.target.value)} style={inp}>{["Net 15", "Net 30", "Net 45", "Net 60", "Due on receipt"].map(t => <option key={t} value={t}>{t}</option>)}</select>)}
      {F("Due date", <input type="date" value={due} onChange={e => setDue(e.target.value)} style={inp} />)}
      {F("Charges through (as-of)", <input type="date" value={chargesThrough} onChange={e => setChargesThrough(e.target.value)} style={inp} />)}
      {F("Issue date", <input type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} style={inp} />)}
      {F("PO #", T(po, setPo))}
      {F("Client reference # (shows on invoice)", T(clientRef, setClientRef))}

      <div style={sec}>Bill To</div>
      {F("Bill-To name", T(billTo, setBillTo, "Company / underwriter"))}
      {F("Attention", T(attn, setAttn))}
      {F("Address", T(address, setAddress))}
      <div style={{ display: "flex", gap: 6 }}>
        <div style={{ flex: 2 }}>{F("City", T(city, setCity))}</div>
        <div style={{ flex: 1 }}>{F("State", T(statev, setStatev))}</div>
        <div style={{ flex: 1 }}>{F("Zip", T(zip, setZip))}</div>
      </div>
      {F("Billing email", T(email, setEmail))}
      {F("CC email", T(cc, setCc))}

      <div style={sec}>Notes</div>
      {F("Note to client (shows on invoice)", <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} style={{ ...inp, resize: "vertical" }} placeholder="optional" />)}
      {F("Remittance instructions (overrides default)", <textarea value={remit} onChange={e => setRemit(e.target.value)} rows={2} style={{ ...inp, resize: "vertical" }} placeholder="optional" />)}
      {F("Internal notes (Ops-only · never on the PDF)", <textarea value={internal} onChange={e => setInternal(e.target.value)} rows={2} style={{ ...inp, resize: "vertical" }} placeholder="optional" />)}

      <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
        <button className="btn btn-primary btn-sm" disabled={busy} onClick={save}>Save</button>
        <button className="btn btn-outline btn-sm" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function WsRow({ label, value }) {
  return (<tr style={{ borderTop: "1px solid var(--border)" }}>
    <td style={{ padding: "6px 8px", fontSize: 11, color: "var(--text3)", textTransform: "uppercase", whiteSpace: "nowrap", verticalAlign: "top", width: "38%" }}>{label}</td>
    <td style={{ padding: "6px 8px", fontSize: 13 }}>{value}</td></tr>);
}

// INV-WS-1B: embed the real generated PDF (the source of truth after the POLISH
// rounds) rather than re-implementing the layout in HTML. Fetches the authed PDF
// blob and renders it in an iframe; re-fetches when the invoice changes (version).
function InvoicePdfPreview({ invId, version }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false; let objUrl = null;
    setLoading(true); setErr(null); setUrl(null);
    apiFetch(`/fee-invoices/${invId}/pdf`, { raw: true })
      .then(async res => { if (!res.ok) throw new Error(await res.text()); return res.blob(); })
      .then(blob => { if (cancelled) return; objUrl = URL.createObjectURL(blob); setUrl(objUrl); })
      .catch(e => { if (!cancelled) setErr(String(e.message || e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [invId, version]);
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden", background: "#525659", minHeight: 300 }}>
      {loading && <div className="empty-state" style={{ padding: 40, color: "#e2e8f0" }}>Rendering invoice…</div>}
      {err && <div className="empty-state" style={{ padding: 24, color: "#fca5a5" }}>Couldn't render the PDF — {err}</div>}
      {url && <iframe title="Invoice PDF preview" src={url} style={{ width: "100%", height: "80vh", border: "none", display: "block" }} />}
    </div>
  );
}
